import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../../../api/apiClient";
import { API_ENDPOINTS } from "../../../api/header";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { ReactSelect } from "@/components/ui/react-select";
import { TableCard } from "@/components/ui/table-card";
import { FilterCard, FilterSearch } from "@/components/ui/filter-card";
import { toast } from "sonner";
import TemplatePreviewModal from "../templatePreviewModal";
import {
  ASSIGNABLE_TEMPLATE_GROUPS,
  ASSIGNABLE_TEMPLATE_TYPES,
  invalidateAllPrinterQueries,
} from "./printerConstants";
import {
  Printer,
  Ruler,
  FileText,
  Eye,
  Settings2,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
  Sparkles,
  ArrowRight,
  RefreshCw,
} from "lucide-react";
import { cn } from "@/lib/utils";

export default function DocumentRoutingMap({
  printers = [],
  allPaperSizes = [],
  paperSizeAssignments,
  printersLoading = false,
}) {
  const queryClient = useQueryClient();
  const currentStore = useSelector((state) => state.current_store);
  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (p) => {
    if (!permissions) return false;
    if (Array.isArray(p)) return p.some((perm) => permissions.includes(perm));
    return permissions.includes(p);
  };
  const canUpdate = hasPermission("printer update");

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("all");
  const [previewTemplate, setPreviewTemplate] = useState(null);

  // Quick Routing Dialog State
  const [routeTargetDoc, setRouteTargetDoc] = useState(null);
  const [routePrinterId, setRoutePrinterId] = useState("");
  const [routePaperSizeId, setRoutePaperSizeId] = useState("");
  const [routeTemplateId, setRouteTemplateId] = useState("");

  const storeId = currentStore?.id;

  // Query templates library
  const { data: allTemplates = [], isLoading: templatesLoading } = useQuery({
    queryKey: ["templates", storeId],
    queryFn: async () => {
      const params = {};
      if (storeId) params.store_id = storeId;
      const res = await apiClient.get(API_ENDPOINTS.TEMPLATES.TEMPLATES, {
        params,
      });
      return res.data?.data ?? res.data ?? [];
    },
    enabled: hasPermission("template read"),
  });

  // Query all assignments across paper sizes if not supplied via props
  const {
    data: fetchedAssignmentsMap = {},
    isLoading: assignmentsMapLoading,
    refetch: refetchAssignments,
  } = useQuery({
    queryKey: ["all-paper-size-assignments", allPaperSizes.map((p) => p.id).join(",")],
    queryFn: async () => {
      if (allPaperSizes.length === 0) return {};
      const map = {};

      const reqs = allPaperSizes.map(async (ps) => {
        try {
          const res = await apiClient.get(
            API_ENDPOINTS.PAPER_SIZES.TEMPLATES(ps.id)
          );
          const list = res.data?.data ?? res.data ?? [];
          list.forEach((item) => {
            if (item.template_type && item.is_active !== false) {
              map[item.template_type] = {
                assignment: item,
                paperSize: ps,
              };
            }
          });
        } catch {
          // ignore
        }
      });

      await Promise.all(reqs);
      return map;
    },
    enabled: allPaperSizes.length > 0 && !paperSizeAssignments,
    staleTime: 30000,
  });

  // Harmonized assignments map
  const allAssignmentsMap = useMemo(() => {
    if (paperSizeAssignments && typeof paperSizeAssignments === "object") {
      const map = {};
      allPaperSizes.forEach((ps) => {
        const list = paperSizeAssignments[ps.id] || [];
        list.forEach((item) => {
          if (item.template_type && item.is_active !== false) {
            map[item.template_type] = {
              assignment: item,
              paperSize: ps,
            };
          }
        });
      });
      return map;
    }
    return fetchedAssignmentsMap;
  }, [paperSizeAssignments, allPaperSizes, fetchedAssignmentsMap]);

  // Default printer for store
  const defaultPrinter = useMemo(
    () => printers.find((p) => p.is_default) || printers[0] || null,
    [printers]
  );

  // Filtered document list
  const filteredDocuments = useMemo(() => {
    return ASSIGNABLE_TEMPLATE_TYPES.filter((item) => {
      if (selectedGroup !== "all") {
        const foundGroup = ASSIGNABLE_TEMPLATE_GROUPS.find(
          (g) => g.id === selectedGroup
        );
        if (foundGroup && item.group !== foundGroup.groupName) {
          return false;
        }
      }

      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        return (
          item.label.toLowerCase().includes(q) ||
          item.description?.toLowerCase().includes(q) ||
          item.value.toLowerCase().includes(q)
        );
      }

      return true;
    });
  }, [selectedGroup, searchQuery]);

  // Open Quick Route Modal
  const handleOpenRouteDialog = (doc) => {
    const existing = allAssignmentsMap[doc.value];
    const initialPrinterId =
      existing?.paperSize?.printer_id || defaultPrinter?.id || printers[0]?.id || "";
    const initialPaperSizeId =
      existing?.paperSize?.id ||
      allPaperSizes.find((p) => p.printer_id === initialPrinterId)?.id ||
      "";
    const initialTemplateId =
      existing?.assignment?.template_content_id ||
      allTemplates.find((t) => t.type === doc.value && t.is_active !== false)?.id ||
      "";

    setRouteTargetDoc(doc);
    setRoutePrinterId(initialPrinterId);
    setRoutePaperSizeId(initialPaperSizeId);
    setRouteTemplateId(initialTemplateId);
  };

  // Available paper sizes for selected printer in dialog
  const dialogPaperSizes = useMemo(() => {
    if (!routePrinterId) return [];
    return allPaperSizes.filter((ps) => ps.printer_id === routePrinterId);
  }, [allPaperSizes, routePrinterId]);

  // Available templates for the document type in dialog
  const dialogTemplates = useMemo(() => {
    if (!routeTargetDoc) return [];
    return allTemplates.filter(
      (t) => t.type === routeTargetDoc.value && t.is_active !== false
    );
  }, [allTemplates, routeTargetDoc]);

  // Save Quick Routing Mutation
  const saveRoutingMutation = useMutation({
    mutationFn: async () => {
      if (!canUpdate) {
        throw new Error("You do not have permission to update document routing");
      }
      if (!routePaperSizeId || !routeTargetDoc) {
        throw new Error("Please select a paper size");
      }

      // 1. Fetch current assignments for this paper size
      const res = await apiClient.get(
        API_ENDPOINTS.PAPER_SIZES.TEMPLATES(routePaperSizeId)
      );
      const currentList = res.data?.data ?? res.data ?? [];

      // 2. Merge or replace this document type
      const nextMap = {};
      currentList.forEach((item) => {
        if (item.template_type && item.template_content_id) {
          nextMap[item.template_type] = {
            template_type: item.template_type,
            template_content_id: item.template_content_id,
            is_active: item.is_active !== false,
            meta: item.meta,
          };
        }
      });

      if (routeTemplateId) {
        nextMap[routeTargetDoc.value] = {
          template_type: routeTargetDoc.value,
          template_content_id: routeTemplateId,
          is_active: true,
          meta: null,
        };
      } else {
        delete nextMap[routeTargetDoc.value];
      }

      // 3. Save full assignments list
      const payload = {
        assignments: Object.values(nextMap),
      };

      return apiClient.put(
        API_ENDPOINTS.PAPER_SIZES.TEMPLATES(routePaperSizeId),
        payload
      );
    },
    onSuccess: () => {
      toast.success(
        `Routing updated for "${routeTargetDoc?.label}"!`
      );
      invalidateAllPrinterQueries(queryClient);
      setRouteTargetDoc(null);
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || err?.message || "Failed to update routing"
      );
    },
  });

  const isLoading = printersLoading || assignmentsMapLoading || templatesLoading;

  return (
    <div className="space-y-4">
      {/* Search & Category Filter Header */}
      <FilterCard>
        <div className="flex flex-col sm:flex-row gap-3 items-center justify-between w-full">
          <div className="w-full sm:w-72">
            <FilterSearch
              placeholder="Search document routing…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-1.5 overflow-x-auto w-full sm:w-auto pb-1 sm:pb-0">
            {ASSIGNABLE_TEMPLATE_GROUPS.map((grp) => {
              const isSelected = selectedGroup === grp.id;
              return (
                <button
                  key={grp.id}
                  type="button"
                  onClick={() => setSelectedGroup(grp.id)}
                  className={cn(
                    "px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap cursor-pointer",
                    isSelected
                      ? "bg-primary text-primary-foreground shadow-xs"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  )}
                >
                  {grp.label}
                </button>
              );
            })}
          </div>
        </div>
      </FilterCard>

      {/* Overview Info Banner */}
      <div className="bg-muted/30 border border-border/80 rounded-xl p-3.5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs">
        <div className="flex items-center gap-2.5">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <p className="font-semibold text-foreground">
              Document Routing Architecture
            </p>
            <p className="text-muted-foreground mt-0.5">
              Documents automatically resolve to assigned printer paper sizes. If unassigned, they use your store's default printer ({defaultPrinter?.name || "None"}).
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetchAssignments()}
          className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-foreground shrink-0 cursor-pointer"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh Map
        </Button>
      </div>

      {/* Main Routing Table */}
      <TableCard>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-[280px]">Document Type</TableHead>
                <TableHead className="w-[140px]">Group</TableHead>
                <TableHead className="w-[220px]">Destination Printer</TableHead>
                <TableHead className="w-[180px]">Paper Size</TableHead>
                <TableHead className="min-w-[200px]">Assigned Template</TableHead>
                <TableHead className="w-[120px]">Status</TableHead>
                <TableHead className="w-[140px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-40 text-center">
                    <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground">
                      <Spinner className="h-6 w-6" />
                      <span className="text-xs">Loading document routing map…</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredDocuments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-32 text-center text-muted-foreground text-xs">
                    No document types match your search.
                  </TableCell>
                </TableRow>
              ) : (
                filteredDocuments.map((doc) => {
                  const assignmentData = allAssignmentsMap[doc.value];
                  const isExplicit = Boolean(assignmentData?.assignment);

                  // Target printer
                  const targetPrinter = isExplicit
                    ? printers.find((p) => p.id === assignmentData.paperSize?.printer_id) || {
                      name: assignmentData.paperSize?.printer_name || "Assigned Printer",
                    }
                    : defaultPrinter;

                  // Target paper size
                  const targetPaperSize = isExplicit
                    ? assignmentData.paperSize
                    : allPaperSizes.find((p) => p.printer_id === defaultPrinter?.id && p.is_default) ||
                    allPaperSizes.find((p) => p.printer_id === defaultPrinter?.id);

                  // Target template
                  const matchingTemplate = isExplicit
                    ? assignmentData.assignment.template_content ||
                    allTemplates.find(
                      (t) => t.id === assignmentData.assignment.template_content_id
                    ) || {
                      name:
                        assignmentData.assignment.template_type_label ||
                        "Custom Assigned Template",
                      type: doc.value,
                    }
                    : allTemplates.find(
                      (t) => t.type === doc.value && t.is_active !== false
                    ) || {
                      name: "System Default Template",
                      type: doc.value,
                      is_system: true,
                    };

                  return (
                    <TableRow key={doc.value} className="hover:bg-muted/30">
                      {/* Document Type */}
                      <TableCell>
                        <div>
                          <p className="font-semibold text-xs text-foreground">
                            {doc.label}
                          </p>
                          <p className="text-[11px] text-muted-foreground line-clamp-1 mt-0.5">
                            {doc.description}
                          </p>
                        </div>
                      </TableCell>

                      {/* Group */}
                      <TableCell>
                        <span className="text-xs text-muted-foreground">
                          {doc.group}
                        </span>
                      </TableCell>

                      {/* Destination Printer */}
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-muted text-foreground shrink-0">
                            <Printer className="h-3.5 w-3.5 text-muted-foreground" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-foreground truncate">
                              {targetPrinter?.name || "No Printer Configured"}
                            </p>
                            {!isExplicit && defaultPrinter && (
                              <span className="text-[10px] text-muted-foreground">
                                (Store Default)
                              </span>
                            )}
                          </div>
                        </div>
                      </TableCell>

                      {/* Paper Size */}
                      <TableCell>
                        {targetPaperSize ? (
                          <div className="flex items-center gap-1.5 text-xs text-foreground">
                            <Ruler className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                            <span className="truncate">{targetPaperSize.label}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>

                      {/* Assigned Template */}
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-foreground truncate max-w-[180px]">
                            {matchingTemplate?.name || (
                              <span className="text-muted-foreground italic">System Default</span>
                            )}
                          </span>
                          {hasPermission("template read") && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-muted-foreground hover:text-foreground shrink-0 cursor-pointer"
                              onClick={() =>
                                setPreviewTemplate(
                                  matchingTemplate || {
                                    type: doc.value,
                                    name: `${doc.label} (Default)`,
                                  }
                                )
                              }
                              title={
                                matchingTemplate
                                  ? "Preview this template"
                                  : "Preview system default template"
                              }
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        {isExplicit ? (
                          <Badge
                            variant="default"
                            className="text-[10px] font-medium py-0 px-1.5 h-4.5 bg-emerald-600 hover:bg-emerald-600 gap-1"
                          >
                            <CheckCircle2 className="h-2.5 w-2.5" />
                            Configured
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="text-[10px] font-normal py-0 px-1.5 h-4.5 text-muted-foreground border-dashed gap-1"
                          >
                            <AlertCircle className="h-2.5 w-2.5 text-amber-500" />
                            Fallback
                          </Badge>
                        )}
                      </TableCell>

                      {/* Actions */}
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {canUpdate && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 px-2.5 text-xs gap-1 font-medium hover:bg-primary/10 hover:text-primary cursor-pointer"
                              onClick={() => handleOpenRouteDialog(doc)}
                            >
                              <Settings2 className="h-3 w-3" />
                              Route
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </TableCard>

      {/* Quick Route Modal */}
      {routeTargetDoc && (
        <Dialog
          open={Boolean(routeTargetDoc)}
          onOpenChange={(o) => !o && setRouteTargetDoc(null)}
        >
          <DialogContent className="max-w-lg bg-background p-0 gap-0 overflow-hidden">
            <DialogHeader className="px-6 py-4 border-b border-border bg-card">
              <DialogTitle className="text-base font-semibold flex items-center gap-2">
                <Settings2 className="h-4 w-4 text-primary" />
                Route Document: {routeTargetDoc.label}
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground mt-0.5">
                Choose which physical printer, paper tray, and template handles this document.
              </DialogDescription>
            </DialogHeader>

            <div className="px-6 py-5 space-y-4">
              {/* Select Printer */}
              <div className="space-y-1.5">
                <Label className="text-xs">Destination Printer</Label>
                <ReactSelect
                  options={printers.map((p) => ({
                    value: p.id,
                    label: `${p.name}${p.is_default ? " (Store Default)" : ""}`,
                  }))}
                  value={
                    printers
                      .map((p) => ({
                        value: p.id,
                        label: `${p.name}${p.is_default ? " (Store Default)" : ""}`,
                      }))
                      .find((p) => p.value === routePrinterId) || null
                  }
                  onChange={(opt) => {
                    const newPrinterId = opt?.value || "";
                    setRoutePrinterId(newPrinterId);
                    const matchingPaper = allPaperSizes.find(
                      (p) => p.printer_id === newPrinterId
                    );
                    setRoutePaperSizeId(matchingPaper?.id || "");
                  }}
                  placeholder="Select printer…"
                />
              </div>

              {/* Select Paper Size */}
              <div className="space-y-1.5">
                <Label className="text-xs">Paper Size / Tray</Label>
                {dialogPaperSizes.length > 0 ? (
                  <ReactSelect
                    options={dialogPaperSizes.map((ps) => ({
                      value: ps.id,
                      label: `${ps.label} (${ps.size.toUpperCase()})${ps.is_default ? " [Default]" : ""
                        }`,
                    }))}
                    value={
                      dialogPaperSizes
                        .map((ps) => ({
                          value: ps.id,
                          label: `${ps.label} (${ps.size.toUpperCase()})${ps.is_default ? " [Default]" : ""
                            }`,
                        }))
                        .find((ps) => ps.value === routePaperSizeId) || null
                    }
                    onChange={(opt) => setRoutePaperSizeId(opt?.value || "")}
                    placeholder="Select paper size…"
                  />
                ) : (
                  <div className="p-3 rounded-lg border border-dashed border-border text-xs text-muted-foreground text-center">
                    No paper sizes configured on this printer.
                  </div>
                )}
              </div>

              {/* Select Template */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Assigned Template</Label>
                  {hasPermission("template read") && (
                    routeTemplateId ? (
                      <button
                        type="button"
                        className="text-[11px] text-primary hover:underline flex items-center gap-1 cursor-pointer"
                        onClick={() => {
                          const t = dialogTemplates.find((t) => t.id === routeTemplateId);
                          if (t) setPreviewTemplate(t);
                        }}
                      >
                        <Eye className="h-3 w-3" /> Preview Selected
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="text-[11px] text-muted-foreground hover:underline flex items-center gap-1 cursor-pointer"
                        onClick={() => {
                          setPreviewTemplate({
                            type: routeTargetDoc?.value,
                            name: `${routeTargetDoc?.label} (System Default)`,
                          });
                        }}
                      >
                        <Eye className="h-3 w-3" /> Preview Default
                      </button>
                    )
                  )}
                </div>
                <ReactSelect
                  options={[
                    { value: "", label: "- System Default (Fallback) -" },
                    ...dialogTemplates.map((t) => ({
                      value: t.id,
                      label: `${t.name}${t.store_id
                        ? " [Store]"
                        : t.is_system
                          ? " [System]"
                          : " [Shared]"
                        }`,
                    })),
                  ]}
                  value={
                    [
                      { value: "", label: "- System Default (Fallback) -" },
                      ...dialogTemplates.map((t) => ({
                        value: t.id,
                        label: `${t.name}${t.store_id
                          ? " [Store]"
                          : t.is_system
                            ? " [System]"
                            : " [Shared]"
                          }`,
                      })),
                    ].find((t) => t.value === routeTemplateId) || null
                  }
                  onChange={(opt) => setRouteTemplateId(opt?.value || "")}
                  placeholder="Select template design…"
                />
              </div>
            </div>

            <DialogFooter className="px-6 py-3 border-t border-border bg-card flex flex-row items-center justify-between sm:justify-between">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setRouteTargetDoc(null)}
                disabled={saveRoutingMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => saveRoutingMutation.mutate()}
                disabled={
                  saveRoutingMutation.isPending ||
                  !canUpdate ||
                  !routePrinterId ||
                  !routePaperSizeId
                }
                className="gap-1.5"
              >
                {saveRoutingMutation.isPending ? (
                  <>
                    <Spinner className="h-3.5 w-3.5" />
                    Applying…
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Apply Routing
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Preview Modal */}
      {previewTemplate && (
        <TemplatePreviewModal
          template={previewTemplate}
          open={Boolean(previewTemplate)}
          onClose={() => setPreviewTemplate(null)}
        />
      )}
    </div>
  );
}
